<template>
	<div class="device-modal-overlay" v-if="visible" @click="handleClose">
		<div class="device-modal" @click.stop>
			<!-- 标题栏 -->
			<div class="modal-header">
				<div class="header-title">
					<div class="cn-title">{{ deviceInfo.label }}</div>
				</div>
				<img class="close-btn" @click="handleClose" src="@/assets/close.png" alt="" />
			</div>

			<!-- 主体内容 -->
			<div class="modal-body">
				<!-- 摄像头模式 -->
				<template v-if="deviceInfo.type === 'camera'">
					<div class="camera-section">
						<div class="video-box">
							<div class="video-player" ref="videoPlayer"></div>
						</div>
					</div>
				</template>

				<!-- 传感器模式 -->
				<template v-else>
					<div class="sensor-section">
						<!-- 加载中 -->
						<div class="sensor-loading" v-if="sensorLoading">数据加载中...</div>
						<template v-else>
							<!-- 设备信息 -->
							<div class="sensor-info" v-if="sensorData">
								<div class="info-row">
									<span class="info-label">设备名称</span>
									<span class="info-value">{{ sensorData.deviceName || '--' }}</span>
								</div>
								<div class="info-row">
									<span class="info-label">设备状态</span>
									<span class="info-value">
										<i class="status-dot"
											:class="sensorData.status === '1' ? 'online' : 'offline'"></i>
										{{ sensorData.statusName || '--' }}
									</span>
								</div>
								<div class="info-row">
									<span class="info-label">上报时间</span>
									<span class="info-value">{{ sensorData.reportTime || '--' }}</span>
								</div>
							</div>
							<!-- 因子网格 -->
							<div class="sensor-grid" v-if="sensorFactors.length">
								<div class="sensor-item" v-for="(factor, i) in sensorFactors" :key="i">
									<div class="item-header">
										<div class="factor-name">{{ factor.factorName }}</div>
										<span class="tag">实时</span>
									</div>
									<div class="factor-value">
										{{ factor.factorValue === null || factor.factorValue === undefined ? '--' : factor.factorValue }}
										<span class="unit">{{ factor.factorUnit || '' }}</span>
									</div>
								</div>
							</div>
							<div class="empty-tip" v-if="sensorData && !sensorFactors.length">暂无传感器因子数据</div>
							<div class="empty-tip" v-if="!sensorData">获取数据失败</div>
						</template>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'DeviceDetailModal',
		props: {
			visible: {
				type: Boolean,
				default: false
			},
			// { type: 'camera'|'sensor', label: '中文名', deviceCode: '传感器用', matchKey: '摄像头模糊匹配用' }
			deviceInfo: {
				type: Object,
				default: () => ({})
			},
			// 来自 parkOverviewLeft 的 area 数据（传感器模式已改为直接调 latest-data 接口，此 prop 保留兼容）
			areaData: {
				type: Object,
				default: () => null
			}
		},
		data() {
			return {
				cameraList: [],
				selectedCameraCode: '',
				dropdownOpen: false,
				jessibucaPlayer: null,
				jessibucaLoaded: false,
				// 传感器最新数据（来自 /devices/latest-data 接口返回的 data）
				sensorData: null,
				sensorLoading: false
			}
		},
		computed: {
			currentCamera() {
				return this.cameraList.find(c => c.deviceCode === this.selectedCameraCode) || null
			},
			// 从 latest-data 接口返回的 sensorData 里取因子
			sensorFactors() {
				if (!this.sensorData || !this.sensorData.factors) return []
				return this.sensorData.factors
			}
		},
		watch: {
			visible(val) {
				if (val) {
					this.dropdownOpen = false
					if (this.deviceInfo.type === 'camera') {
						this.loadCameraList()
					} else if (this.deviceInfo.type === 'sensor') {
						this.loadSensorData()
					}
				} else {
					this.destroyPlayer()
					// 关闭时清空传感器数据，避免下次打开闪现旧数据
					this.sensorData = null
					this.sensorLoading = false
				}
			}
		},
		beforeDestroy() {
			this.destroyPlayer()
		},
		methods: {
			handleClose() {
				this.$emit('close')
			},
			// 加载 jessibuca 脚本
			loadJessibuca() {
				return new Promise((resolve, reject) => {
					if (window.Jessibuca) {
						this.jessibucaLoaded = true
						resolve()
						return
					}
					const script = document.createElement('script')
					script.src = '/static/jessibuca/jessibuca.js'
					script.onload = () => {
						this.jessibucaLoaded = true
						resolve()
					}
					script.onerror = (err) => reject(err)
					document.head.appendChild(script)
				})
			},
			// 获取摄像头列表
			loadCameraList() {
				this.$http.get(this.$api.BASE_URL_Category.camera_list, {
					pageNumber: 1,
					pageSize: 1000
				}).then(res => {
					const allList = res.data.data || []
					this.cameraList = allList
					// 根据 deviceInfo.matchKey 模糊匹配 deviceName
					let matched = null
					if (this.deviceInfo.matchKey) {
						matched = allList.find(c =>
							c.deviceName && c.deviceName.indexOf(this.deviceInfo.matchKey) !== -1
						)
					}
					// 匹配不到就取第一个有流的
					if (!matched) {
						matched = allList.find(c => c.flvStreamUrl) || allList[0] || null
					}
					if (matched) {
						this.selectedCameraCode = matched.deviceCode
						this.$nextTick(() => this.initPlayer())
					}
				}).catch(err => {
					console.error('[DeviceDetailModal] 获取摄像头列表失败:', err)
				})
			},
			// 获取传感器最新数据（GET /devices/latest-data，参数 deviceCode）
			loadSensorData() {
				if (!this.deviceInfo.deviceCode) return
				this.sensorLoading = true
				this.sensorData = null
				this.$http.get(this.$api.BASE_URL_Category.latDataCode, {
					deviceCode: this.deviceInfo.deviceCode
				}).then(res => {
					// 响应拦截器返回 { msg, code, data }，业务数据在 res.data
					this.sensorData = (res && res.data) || null
					// 把设备状态回传给父组件，驱动模型发光点颜色（在线绿 / 故障告警离线红）
					if (this.sensorData && this.sensorData.status !== undefined) {
						this.$emit('statusChange', {
							deviceCode: this.deviceInfo.deviceCode,
							status: this.sensorData.status
						})
					}
				}).catch(err => {
					console.error('[DeviceDetailModal] 获取传感器数据失败:', err)
					this.sensorData = null
				}).finally(() => {
					this.sensorLoading = false
				})
			},
			selectCamera(cam) {
				this.dropdownOpen = false
				if (this.selectedCameraCode === cam.deviceCode) return
				this.selectedCameraCode = cam.deviceCode
				this.destroyPlayer()
				this.$nextTick(() => this.initPlayer())
			},
			// 初始化 jessibuca 播放器
			async initPlayer() {
				const container = this.$refs.videoPlayer
				if (!container || !this.currentCamera || !this.currentCamera.flvStreamUrl) return

				// 等容器有真实尺寸再初始化（弹窗刚渲染时可能还没布局完成）
				const waitContainerReady = () => new Promise((resolve) => {
					const start = Date.now()
					const check = () => {
						const w = container.clientWidth
						const h = container.clientHeight
						if (w > 0 && h > 0) {
							resolve({ w, h })
						} else if (Date.now() - start > 2000) {
							// 2s 还没尺寸，强制继续（容器 CSS 已设固定 800x450，不应发生）
							resolve({ w: w || 800, h: h || 450 })
						} else {
							requestAnimationFrame(check)
						}
					}
					check()
				})

				await waitContainerReady()

				try {
					await this.loadJessibuca()
				} catch (e) {
					console.error('[DeviceDetailModal] jessibuca 加载失败:', e)
					return
				}
				container.innerHTML = ''
				try {
					const decoderUrl = window.location.origin + process.env.BASE_URL + 'static/jessibuca/decoder.js'
					const player = new window.Jessibuca({
						container: container,
						decoder: decoderUrl,
						videoBuffer: 0.2,
						isResize: true,           // 关键：监听容器尺寸变化，自适应
						loadingText: '加载中...',
						useMSE: true,
						useWCS: false,
						autoWasm: true,
						forceNoOffscreen: false,
						isNotMute: true,
						timeout: 10,
						hasAudio: false,
						operateBtns: {
							fullscreen: true,
							screenshot: false,
							play: true,
							audio: false
						},
						controlAutoHide: true,
						debug: false
					})
					player.play(this.currentCamera.flvStreamUrl)
					this.jessibucaPlayer = player

					// 播放成功后再强制 resize 一次，确保 video/canvas 撑满容器
					player.on('play', () => {
						this.$nextTick(() => {
							// 手动触发一次 resize，让 jessibuca 按当前容器尺寸调整
							if (this.jessibucaPlayer && this.jessibucaPlayer.resize) {
								this.jessibucaPlayer.resize()
							}
						})
					})
				} catch (err) {
					console.error('[DeviceDetailModal] 播放器初始化失败:', err)
				}
			},
			destroyPlayer() {
				if (this.jessibucaPlayer) {
					try {
						this.jessibucaPlayer.destroy()
					} catch (e) {
						console.error('[DeviceDetailModal] 销毁播放器失败:', e)
					}
					this.jessibucaPlayer = null
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.device-modal-overlay {
		position: fixed;
		inset: 0;
		z-index: 99999;
		background: rgba(0, 0, 0, 0.6);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.device-modal {
		min-width: 260px;
		background: linear-gradient(180deg, #0a1a2e 0%, #091724 100%);
		border: 1px solid #2C3D52;
		border-radius: 12px;
		box-shadow: 0 8px 40px rgba(0, 150, 255, 0.15);
		display: flex;
		flex-direction: column;
		overflow: hidden;

		.modal-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 6px 10px;
			background: linear-gradient(180deg, #0d2340 0%, #091724 100%);
			border-bottom: 1px solid #2C3D52;

			.header-title {
				.en-title {
					font-size: 10px;
					color: #4a6a8a;
					letter-spacing: 1px;
				}

				.cn-title {
					font-size: 14px;
					font-weight: bold;
					color: #B3C8DC;
					margin-top: 2px;
				}
			}

			.close-btn {
				width: 20px;
				height: 20px;
				cursor: pointer;
				opacity: 0.7;
				transition: opacity 0.2s;

				&:hover {
					opacity: 1;
				}
			}
		}

		.modal-body {
			padding: 10px;
			overflow-y: auto;
		}
	}

	/* ============ 摄像头模式 ============ */
	.camera-section {
		.camera-select-row {
			display: flex;
			align-items: center;
			gap: 12px;
			margin-bottom: 12px;

			.select-label {
				font-size: 13px;
				color: #7ABFFF;
				white-space: nowrap;
			}

			.select-box {
				flex: 1;
				height: 34px;
				line-height: 34px;
				padding: 0 10px;
				background: #172434;
				border: 1px solid #2C3D52;
				border-radius: 6px;
				font-size: 13px;
				color: #B3C8DC;
				cursor: pointer;
				display: flex;
				align-items: center;
				justify-content: space-between;
				position: relative;

				.select-text {
					flex: 1;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

				.arrow {
					width: 14px;
					height: 10px;
					margin-left: 8px;
				}

				.dropdown {
					position: absolute;
					top: calc(100% + 4px);
					left: 0;
					right: 0;
					max-height: 220px;
					overflow-y: auto;
					background: rgba(9, 23, 36, 0.98);
					border: 1px solid #2C3D52;
					border-radius: 6px;
					z-index: 99;
					padding: 4px 0;
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);

					.dropdown-item {
						padding: 7px 10px;
						display: flex;
						justify-content: space-between;
						align-items: center;
						gap: 8px;
						cursor: pointer;
						font-size: 12px;
						color: #B3C8DC;
						transition: all 0.2s;

						.name {
							flex: 1;
							overflow: hidden;
							text-overflow: ellipsis;
							white-space: nowrap;
						}

						.area {
							font-size: 10px;
							color: #7ABFFF;
							flex-shrink: 0;
						}

						&:hover {
							background: rgba(69, 120, 168, 0.3);
						}

						&.active {
							background: rgba(54, 209, 255, 0.2);
							border-left: 2px solid #36d1ff;

							.name {
								color: #36d1ff;
							}
						}
					}

					.dropdown-empty {
						padding: 12px;
						text-align: center;
						color: #5a7a96;
						font-size: 12px;
					}

					&::-webkit-scrollbar {
						width: 4px;
					}

					&::-webkit-scrollbar-thumb {
						background: #4578A8;
						border-radius: 2px;
					}
				}
			}
		}

		.video-box {
			width: 800px;
			height: 450px;
			background: #000;
			border-radius: 8px;
			position: relative;
			overflow: hidden;
			border: 1px solid #2C3D52;

		.video-player {
			width: 100%;
			height: 100%;
			position: relative;
		}

		.no-signal {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			color: #5a7a96;
			font-size: 14px;
			z-index: 1;
			pointer-events: none;
		}

		// jessibuca 创建的 video/canvas 必须撑满容器
		::v-deep canvas,
		::v-deep video {
			width: 100% !important;
			height: 100% !important;
			top: 0 !important;
			left: 0 !important;
			transform:none !important;
			// display: block !important;
			// object-fit: cover;
		}
		}

		.camera-info {
			display: flex;
			justify-content: space-around;
			margin-top: 12px;
			padding: 10px 0;
			border-top: 1px solid #1a2e44;

			.info-item {
				text-align: center;

				.label {
					display: block;
					font-size: 10px;
					color: #5a7a96;
					margin-bottom: 4px;
				}

				.value {
					font-size: 13px;
					color: #B3C8DC;
					font-weight: 500;

					&.online {
						color: #4A98F7;
					}
				}
			}
		}
	}

	/* ============ 传感器模式 ============ */
	.sensor-section {
		.sensor-loading {
			text-align: center;
			color: #5a7a96;
			font-size: 13px;
			padding: 30px 0;
		}

		.sensor-info {
			padding: 8px 10px;
			margin-bottom: 10px;
			background: linear-gradient(0deg, #081724 0%, #16283A 100%);
			border: 1px solid #1a2e44;
			border-radius: 8px;

			.info-row {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 3px 0;
				font-size: 11px;

				.info-label {
					color: #5a7a96;
					flex-shrink: 0;
				}

				.info-value {
					color: #B3C8DC;
					display: flex;
					align-items: center;
					gap: 4px;
					text-align: right;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
			}
		}

		.status-dot {
			display: inline-block;
			width: 6px;
			height: 6px;
			border-radius: 50%;
			flex-shrink: 0;

			&.online {
				background: #36d1ff;
				box-shadow: 0 0 6px #36d1ff;
			}

			&.offline {
				background: #ff4444;
			}
		}

		.sensor-grid {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 12px;
		}

		.sensor-item {
			padding: 12px;
			background: linear-gradient(0deg, #081724 0%, #16283A 100%);
			border: 1px solid #1a2e44;
			border-radius: 8px;
			position: relative;

			.item-header {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 8px;

				.factor-name {
					font-size: 12px;
					font-weight: 500;
					color: #B3C8DC;
				}

				.tag {
					font-size: 9px;
					padding: 2px 6px;
					background: linear-gradient(180deg, #091825 0%, #50A4FC 100%);
					border: 1px solid #7ABFFF;
					border-radius: 0 6px 0 6px;
					color: #fff;
				}
			}

			.factor-value {
				font-size: 22px;
				font-weight: 800;
				color: #D2E9FF;
				line-height: 32px;

				.unit {
					font-size: 12px;
					font-weight: 400;
					color: #7ABFFF;
					margin-left: 4px;
				}
			}

			.device-name {
				font-size: 10px;
				color: #5a7a96;
				margin-top: 4px;
			}
		}

		.empty-tip {
			text-align: center;
			color: #5a7a96;
			font-size: 14px;
			padding: 30px 0;
		}
	}
</style>